import { useSearchParams, useNavigate } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import indexApi from '@/apis/index'
import { retry, navigateToExternal } from '@/utils/utils'
import { RouteCodeDirectDrainageContext, useAdChannelStore } from '@/store/adChannelCode.store'
import { FLG_UNION_ID, FLG_UNION_NAME, ROUTE_CODE_DIRECT_DRAINAGE_CONTEXT } from '@/constants/storage'
import activityApi from '@/apis/activity'
// import fingerprintjs from '@fingerprintjs/fingerprintjs'
import {
  buildFailureUrl,
  buildRouteCodeConvertParams,
  initEntryOriginalUrl,
  InvalidLinkReasonText,
  saveInvalidLinkContext,
  useSensors
} from './use-sensors'
import { YesNo } from '@/constants/user'
import { Storage } from '@/utils/common-js'

export const useInitializeAdChannelCode = () => {
  const [search, setSearch] = useSearchParams()
  const { visitLoadingPageSensors, trackRouteCodeConvert } = useSensors()
  const { getCodeDetails, setRouteCodeDetail, clearRouteCodeDetail, restoreRouteCodeDetail } = useAdChannelStore()
  const navigator = useNavigate()
  const isInitialized = useRef(false)

  // sessionStorage 保存的是“路由码 -> 渠道码”的导流上下文，用于从下游返回后继续导流。
  const getRouteCodeDirectDrainageContext = (): RouteCodeDirectDrainageContext | null => {
    try {
      const context = sessionStorage.getItem(ROUTE_CODE_DIRECT_DRAINAGE_CONTEXT)
      return context ? JSON.parse(context) : null
    } catch (error) {
      console.error('路由码导流上下文解析失败', error)
      return null
    }
  }

  const handleRedirect = async(code:string) => {
    try {
      visitLoadingPageSensors(code)
      const fp = await import('@fingerprintjs/fingerprintjs').then(fp => fp.load())
      const { visitorId } = await fp.get()
      await activityApi.reportAdChannelClick({
        adChannelCode: code,
        fingerprint: visitorId,
        abTestType: 17
      })
    } catch (error) {
      console.error('Redirect failed:', error)
    }
  }

  const initAdChannelCode = async () => {
    if (isInitialized.current) return
    isInitialized.current = true
    const originalUrl = window.location.href
    initEntryOriginalUrl(originalUrl)
    const adRouteCode = search.get('adRouteCode')
    const adChannelCode = search.get('adChannelCode')
    let newAdChannelCode = ''
    if (adRouteCode && !adChannelCode) {
      // 路由码分配渠道码
      let res
      try {
        res = await retry(() => indexApi.getAdChannelCodeByRouteCode(adRouteCode))
      } catch (error) {
        trackRouteCodeConvert(buildRouteCodeConvertParams({
          originalUrl,
          adRouteCode,
          finalUrl: buildFailureUrl(originalUrl),
          error
        }))
        throw error
      }
      const newSearch = new URLSearchParams(search)
      newSearch.set('adChannelCode', res.adChannelCode || '')
      newSearch.delete('adRouteCode')
      const nextUrl = `${window.location.origin}${window.location.pathname}?${newSearch.toString()}`
      const finalUrl = res.planTargetUrl || (!res.flgOnline ? buildFailureUrl(originalUrl) : nextUrl)
      trackRouteCodeConvert(buildRouteCodeConvertParams({
        originalUrl,
        adRouteCode,
        response: res,
        finalUrl
      }))
      if (!res.flgOnline) {
        saveInvalidLinkContext({
          original_url: originalUrl,
          before_failure_url: window.location.href,
          invalid_link_reason: InvalidLinkReasonText.RouteCodeOffline,
          ad_route_code: adRouteCode,
          ad_channel_code: res.adChannelCode || ''
        })
        navigator('/failure', { replace: true })
        return
      }
      newAdChannelCode = res.adChannelCode || ''
      // 路由码兑换成功后 URL 会被替换成 adChannelCode，原始路由码需要单独保存到会话上下文。
      setRouteCodeDetail(adRouteCode, res)
      if (res.crmJumpUrl && !res.planTargetUrl) {
        // 分配到的渠道码导流给CRM跳转
        Storage.set(`${newAdChannelCode}_CRM_JUMP_WECHAT`, { crmJumpUrl: res.crmJumpUrl, productId: res.productId })
      } else if (res.planTargetUrl) {
        // 分配到的渠道码导流非联登产品 直接跳转
        await handleRedirect(newAdChannelCode)
        navigateToExternal(res.planTargetUrl)
        return
      }
      if (res.flgUnion === YesNo.Yes && res.productId) {
        // 分配到的渠道码导流联登产品，保存productId用于后续联登使用
        Storage.set(FLG_UNION_ID, res.productId)
        Storage.set(FLG_UNION_NAME, res.productName)
      }
      navigator(`?${newSearch.toString()}`,{replace:true})
    } else {
      newAdChannelCode = search.get('adChannelCode')
        || search.get('source')
        || search.get('apiChannelCode')
        || localStorage.getItem('ad_channel_code')
        || ''
    }
    const routeCodeDirectDrainageContext = getRouteCodeDirectDrainageContext()
    // 只在当前渠道码等于路由码兑换出的渠道码时恢复，避免旧路由码污染新的渠道码入口。
    if (routeCodeDirectDrainageContext?.routeCodeDetail?.adChannelCode === newAdChannelCode) {
      restoreRouteCodeDetail(routeCodeDirectDrainageContext)
    } else if (routeCodeDirectDrainageContext) {
      clearRouteCodeDetail()
    }
    localStorage.setItem('ad_channel_code', newAdChannelCode)
    useAdChannelStore.getState().setAdChannelCode(newAdChannelCode)
    useAdChannelStore.getState().checkSpecialChannel()
    await getCodeDetails(newAdChannelCode)
  }

  useEffect(() => {
    initAdChannelCode()
  }, [])
}
